import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    GridColumn,
    useSnackbar
} from "@livingdesign/react";
import MuiDataTable from "../../../../components/Table/MuiDataTable/MuiDataTable";
import {
    DOWNLOAD_NOTE_FILENAME,
    ENABLE_SERVER_SIDE_FEATURES_NOTE,
    ROWS_PER_PAGE_NOTE,
    ROWS_PER_PAGE_OPTIONS_NOTE,
} from "../../../../constants/tableConfigs";
import { getErrorMessage } from "../../../../helpers/utils";
import { formSearchQuery } from "../../../../helpers/tableUtils";
import { Operations } from "../../../../axios/operations";
import { getService } from "../../../../axios/axios";
import { NOTE_SEARCH_SERVICE } from "../../../../constants/baseURLs";
import { displayLocalDate } from "../../../../helpers/utcUtils";
import { setInvoiceNoteData } from "../../../../store/actions/noteActions";
import { TAB_LABEL } from "../../../../constants/tabLabel";
import InvoiceNotesListStyles from "./InvoiceNotesList.styles";
import { CONTENTS, CREATED, CREATOR, DESCRIPTION, FIND_A_TAG, NOTE_ID, SHOW_NO_RESULT, TAGGED_WITH, TITLE } from "../../../../constants/constants";

/**
 * Function to form search query from user entered search string
 *
 * @param {string} searchText
 * @returns search query
 */
export const getSearchQuery = (
    searchText,
    COLUMNS
) => {

    let query = "";
    COLUMNS?.forEach((column, index) => {
        if (column?.options?.display) {
            const colQuery = formSearchQuery(column?.name, column?.operation, searchText, column?.type);
            if (colQuery !== "") {
                query += query ? " OR " + colQuery : colQuery;
            }
        }
    });
    return query;
};

export const displayContent = (value, tableMeta, cellId) => {
    return (
        <GridColumn lg={12} key={value + tableMeta?.rowIndex} id={cellId}>
            <div>
                <div> {tableMeta?.rowData?.[1]}</div>
            </div>
        </GridColumn>
    )
}

/**
 * Component to render Note List page
 */
const InvoiceNotesList = (props) => {
    const moduleDetails = props?.moduleDetails;
    const parentLoading = props?.parentLoading;
    const COLUMNS = [
        {
            name: "notesId",
            label: NOTE_ID,
            key: "notesId",
            options: {
                sort: false,
                display: true,
            },
            operation: Operations.EQ,
            type: 'id'
        },
        {
            name: "title",
            label: TITLE,
            options: {
                display: false
            },
        },
        {
            name: "description",
            label: DESCRIPTION,
            options: {
                sort: false,
                display: false,
            },
        },
        {
            name: "description",
            label: CONTENTS,
            colKey: "contents",
            options: {
                filter: false,
                sort: false,
                display: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    const cellId = `Mui-note-title-row-${tableMeta?.rowIndex}`;
                    return displayContent(value, tableMeta, cellId);
                },
            },
            operation: Operations.LIKEIC,
            type: "notesContent"
        },
        {
            name: "tags",
            label: TAGGED_WITH,
            colKey: 'tags',
            options: {
                display: true,
                customFilterListOptions: { render: (v) => `Tagged With: ${v}` },
                customBodyRender: (value, tableMeta, updateValue) => {
                    return value?.length > 0 ? value?.join(", ") : "-"
                },
            },
            operation: Operations.LIKEIC,
            type: 'tag'
        },
        {
            name: "createdBy",
            label: CREATOR,
            colKey: "creator",
            options: {
                display: true,
                filterList: [],
                customFilterListOptions: { render: (v) => `Creator: ${v}` }
            },
            operation: Operations.LIKEIC,
            type: "tag"
        },
        {
            name: "createdTs",
            label: CREATED,
            colKey: "created",
            options: {
                display: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    return displayLocalDate(value);
                },
            },
            operation: Operations.GTE,
            type: "date"
        },
        {
            name: "id",
            label: "id",
            options: {
                display: false,
            },
        }
    ];
    const classes = InvoiceNotesListStyles();
    const [loading, setLoading] = useState(false);
    const [rowsPerPageNotes, setRowsPerPageNotes] = useState(ROWS_PER_PAGE_NOTE);
    const [filterQuery, setFilterQuery] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortQuery, setSortQuery] = useState(`&order=createdTs desc`);
    const [notesTableColumns, setNotesTableColumns] = useState(COLUMNS);
    const dispatch = useDispatch();
    const history = useHistory();
    const invoiceNoteData = useSelector((state) => state?.note?.invoiceNoteData);
    const { addSnack } = useSnackbar();

    // Default filter condition for notes
    const defaultFilterConditionNote = `${formSearchQuery(
        "matterNumber",
        Operations.EQ,
        moduleDetails?.id,
        'id'
    )} and ${formSearchQuery(
        "isPrivate",
        Operations.EQ,
        false,
        'boolean',
    )}`;

    /**********
     @types : [
     'checkbox',
     'dropdown',
     'dropdownAutocomplete',
     'autocompleteMultiSelect',
     ]
     **********/
    const FILTER_CONFIGS = [
        {
            colKey: "tags",
            type: "masterDataAutocomplete",
            label: FIND_A_TAG,
            dataType: "noteTags",
            defaultOpen: true,
        },
        {
            colKey: "creator",
            type: "peopleMultiDropdownAutocomplete",
            label: CREATOR,
            defaultOpen: true,
            accordian: 'Creator',
            externalUser: true,
            isInternalExternal: false
        },
    ];

    /** Keeping for API integration
     * Function to call note list Service
     * @param {string} query
     *
     */
    const getNoteData = (query = "") => {
        setLoading(true);
        if (parentLoading) {
            return
        }
        const uri = `(${defaultFilterConditionNote})${searchQuery !== '' ? 'and (' : ''}${searchQuery}${searchQuery !== '' ? ')' : ''}${filterQuery}${sortQuery}${query}`
        const encodedUri = encodeURI(uri)
        getService(
            NOTE_SEARCH_SERVICE,
            `/search/notes/v1?filter=${encodedUri}`
        )
            .then((response) => {
                dispatch(setInvoiceNoteData(response.data));
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                addSnack({
                    message: getErrorMessage(error)
                });
            });
    };

    useEffect(() => {
        let offsetQuery = "";
        if (ENABLE_SERVER_SIDE_FEATURES_NOTE) {
            offsetQuery = `&limit=${rowsPerPageNotes}&offset=0`;
        }
        getNoteData(offsetQuery);
    }, [searchQuery, filterQuery, sortQuery, parentLoading]);


    /**
     * Function to set search query on search action
     * @param {string} searchText
     */
    const handleServerSideSearch = (searchText) => {
        const searchQuery = searchText
            ? getSearchQuery(searchText, COLUMNS)
            : "";
        if (searchQuery !== SHOW_NO_RESULT) {
            setSearchQuery(searchQuery);
        } else {
            setInvoiceNoteData([]);
        }
    };

    /**
     * Gets called when user click on add new event button
     *
     * @Public
     */
    const handleAddNew = (term) => {
        history.push("/invoice/notes/add", { module: "invoice", moduleDetails: moduleDetails, heading: `Add a new note for Invoice: ${moduleDetails?.organizationInvoiceNumber}`, currentTab: TAB_LABEL.NOTES });
    };

    /**
     * Function to set filter query on filter action
     * @param {string} query
     */
    const handleServerSideFilter = (query) => {
        if (query.indexOf("creator") !== -1) {
            query = query.replace("creator", "createdBy");
        }
        setFilterQuery(query);
    };

    /**
     * Function to call api on pagination action
     * @param {number} rowsPerPage
     * @param {number} page
     */
    const handleServerSidePagination = ({ rowsPerPage, page }) => {
        setRowsPerPageNotes(rowsPerPage);
        getNoteData(`&limit=${rowsPerPage}&offset=${rowsPerPage * page}`);
    };

    /**
     * Function to call api on sort action
     * @param {number} rowsPerPage
     * @param {number} page
     */
    const handleServerSideSort = ({ name, direction }) => {
        setSortQuery(`&order=${name} ${direction}`);
    };

    const handleRowClick = (rowData, rowMeta) =>
        history.push(`/invoice/${moduleDetails?.id}/notes/${invoiceNoteData?.notes[rowMeta?.dataIndex]?.id}`, {
            module: "invoice", moduleDetails: moduleDetails, isRowClick: true
        });

    const notesTableCustomOptions = {
        downloadOptions: {
            filename: DOWNLOAD_NOTE_FILENAME,
        },
        rowsPerPage: rowsPerPageNotes,
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS_NOTE,
        filterConfigs: FILTER_CONFIGS,
        serverSide: ENABLE_SERVER_SIDE_FEATURES_NOTE,
        onServerSideSearch: handleServerSideSearch,
        onServerSideFilter: handleServerSideFilter,
        onServerSidePagination: handleServerSidePagination,
        onServerSideSort: handleServerSideSort,
        totalCount: invoiceNoteData?.count,
    };

    return (
        <>
            <div className={classes.pageContent}>
                <MuiDataTable
                    data={invoiceNoteData?.notes}
                    columns={notesTableColumns}
                    customOptions={notesTableCustomOptions}
                    setTableColumns={setNotesTableColumns}
                    onAddNew={handleAddNew}
                    onRowClick={handleRowClick}
                    loading={loading}
                    showRowsCounter={false}
                    disableExport={true}
                />
            </div>
        </>
    );
};

export default InvoiceNotesList;
